import { api } from '../base';
import { 
  FileRecordSchema, 
  PaginatedResponseSchema,
  type FileRecord, 
  type PaginatedResponse 
} from '../types';
import { z } from 'zod';

export class FilesApi {
  async getAll(params?: {
    bucketId?: number;
    page?: number;
    limit?: number;
  }): Promise<PaginatedResponse<FileRecord>> {
    const searchParams = new URLSearchParams();
    if (params?.bucketId) searchParams.set('bucket_id', params.bucketId.toString());
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    
    const query = searchParams.toString();
    const endpoint = `/v1/files${query ? `?${query}` : ''}`;
    
    const response = await api.get(endpoint);
    return PaginatedResponseSchema(FileRecordSchema).parse(response);
  }

  async getByBucket(bucketId: number): Promise<FileRecord[]> {
    const response = await this.getAll({ bucketId });
    return response.items;
  }

  async getByFolder(folderId: number): Promise<FileRecord[]> {
    const response = await this.getAll();
    return response.items.filter(file => file.folder_id === folderId);
  }

  async getById(id: number): Promise<FileRecord> {
    const response = await api.get(`/v1/files/${id}`);
    return FileRecordSchema.parse(response);
  }

  async delete(id: number): Promise<void> {
    await api.delete(`/v1/files/${id}`);
  }

  getDownloadUrl(fileId: number): string {
    return `${api.config.baseURL}/v1/files/${fileId}/download`;
  }

  async download(fileId: number, filename?: string): Promise<void> {
    const url = this.getDownloadUrl(fileId);

    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`Download failed: ${response.statusText}`);
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = downloadUrl;

    if (filename) {
      link.download = filename;
    } else {
      const contentDisposition = response.headers.get('content-disposition') || '';
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        link.download = filenameMatch[1].replace(/['"]/g, '');
      }
    }

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }

  async getBlob(fileId: number): Promise<Blob> {
    const url = `${api.config.baseURL}/v1/files/${fileId}/download?preview=true`;
    
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'X-Request-Type': 'preview',
      },
    });

    if (!response.ok) {
      throw new Error(`File fetch failed: ${response.statusText}`);
    }
    
    return response.blob();
  }

  async getDataUrl(fileId: number): Promise<string> {
    const blob = await this.getBlob(fileId);
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(blob);
    });
  }

  async getTextContent(fileId: number): Promise<string> {
    const blob = await this.getBlob(fileId);
    return blob.text();
  }

  isPreviewable(file: FileRecord): boolean {
    const previewableTypes = [
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml',
      'video/mp4', 'video/webm', 'video/ogg',
      'audio/mpeg', 'audio/ogg', 'audio/wav', 'audio/mp3',
      'application/pdf',
      'text/plain', 'text/html', 'text/css', 'text/javascript',
      'application/json', 'application/xml',
    ];

    if (previewableTypes.includes(file.mime_type || '')) {
      return true;
    }

    // Check by extension
    const ext = file.filename.toLowerCase().split('.').pop() || '';
    const previewableExts = [
      'jpg', 'jpeg', 'png', 'gif', 'webp', 'svg',
      'mp4', 'webm', 'ogg', 'mov',
      'mp3', 'wav', 'ogg', 'aac',
      'pdf',
      'txt', 'md', 'json', 'xml', 'html', 'css', 'js', 'ts', 'jsx', 'tsx',
      'py', 'java', 'cpp', 'c', 'h', 'rs', 'go', 'php', 'rb', 'sh',
    ];

    return previewableExts.includes(ext);
  }

  getPreviewType(file: FileRecord): 'image' | 'video' | 'audio' | 'pdf' | 'text' | 'unsupported' {
    const mimeType = file.mime_type || '';
    const ext = file.filename.toLowerCase().split('.').pop() || '';

    if (mimeType.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) {
      return 'image';
    }
    
    if (mimeType.startsWith('video/') || ['mp4', 'webm', 'ogg', 'mov'].includes(ext)) {
      return 'video';
    }
    
    if (mimeType.startsWith('audio/') || ['mp3', 'wav', 'ogg', 'aac'].includes(ext)) {
      return 'audio';
    }
    
    if (mimeType === 'application/pdf' || ext === 'pdf') {
      return 'pdf';
    }
    
    if (mimeType.startsWith('text/') || [
      'txt', 'md', 'json', 'xml', 'html', 'css', 'js', 'ts', 'jsx', 'tsx',
      'py', 'java', 'cpp', 'c', 'h', 'rs', 'go', 'php', 'rb', 'sh'
    ].includes(ext)) {
      return 'text';
    }

    return 'unsupported';
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 30) return `${diffDays}d ago`;

    return date.toLocaleDateString();
  }
}